import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, DeleteOutline } from "@mui/icons-material";
import * as React from "react";
import { useState } from "react";
import { deleteNewsletter as deleteNewsletterApi } from "apis";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  onClose: () => void;
  id: number;
}

const DeleteNewsletterDialog: React.FC<Props> = ({ open, onClose, id }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);

  const handleDelete = () => {
    setSubmitting(true);
    deleteNewsletterApi({ id })
      .then(() => {
        enqueueSnackbar(t("newsletter.deleted"), { variant: "success" });
        onClose();
      })
      .catch((err) => {
        if (err?.response?.status === 400) {
          enqueueSnackbar(err.response.data.message, { variant: "error" });
        }
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
          color: "common.white",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <DeleteOutline />
          <Typography variant="h6" fontWeight={700}>
            {t("newsletter.delete")}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "common.white" }}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, py: 4, textAlign: "center" }}>
        <Typography color="text.secondary">
          {t("alert.confirm_delete")}
        </Typography>
      </DialogContent>

      {/* Actions */}
      <Box sx={{ px: 3, pb: 3, display: "flex", gap: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          fullWidth
          sx={{
            borderRadius: 2,
            py: 1.2,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {t("buttons.cancel")}
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          fullWidth
          disabled={submitting}
          sx={{
            borderRadius: 2,
            py: 1.2,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": { boxShadow: 2 },
          }}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("buttons.delete")
          )}
        </Button>
      </Box>
    </Dialog>
  );
};

export default DeleteNewsletterDialog;
